/**
 * availabilityExtension + availabilityNodeView — `IMailEditorInsert.addSendAvailability` 의
 * NgwEditor extension (PR-N5).
 *
 * TinyMCE 평행: `nine_tinymce_desktop.js` L7280~L7361 의 `window.addSendAvailability(...)` 가
 * 큰 HTML 단편 (외곽 `div.ms-outlook-mobile-availability-container[id][data-time-tables][itemscope]
 * [itemtype="https://schema.org/ChooseAction"]` + 내부 시간표 nested table) 을 본문에 삽입.
 * NgwEditor 측에서는 ProseMirror leaf atom 노드 + NodeView 로 동일 시맨틱.
 *
 * ### NodeView 의 cancel 버튼 (사용자 결정 (a))
 *
 * cancel 버튼 mousedown 이 두 가지를 동시 수행:
 *   1. `adapter.getHandler()?.clearAvailability?.(id)` — 부모(React) 측에 알림. PR-N6 의
 *      event handler 전수 wiring 진입 전에 NodeView callback 패턴을 사전 노출.
 *   2. `view.dispatch(tr.delete(pos, pos + nodeSize))` — 자체 노드 제거.
 *
 * TinyMCE 평행 (`nine_tinymce_desktop.js` L4908~L4914): 좌표 기반 우측 상단 클릭을 cancel 로 판단
 * → `parentElement.removeChild` + `notifyClearAvailability()`. NgwEditor 는 명시적 cancel 버튼
 * 으로 단순화.
 *
 * ### addSendAvailability 명령의 갱신 vs 신규
 *
 * payload.id 가 제공되면 doc 안에서 동일 id 노드를 찾아 `setNodeMarkup` 으로 attr 갱신. 미매칭 시
 * 신규 노드 삽입 fallback. payload.id 미제공 시 항상 신규 (uuid-like id 발급).
 *
 * ### 보안 — XSS
 *
 * `dateTimeTableJson` 은 ProseMirror node attribute 로만 보존되며, `toDOM` 의 `data-time-tables`
 * attr 값으로만 흐른다. innerHTML 또는 raw HTML 삽입 표면 없음. NodeView 의 title/tableDesc 는
 * `textContent` 로만 설정 — HTML escape 자동 적용.
 */

import {
  type Node as ProseMirrorNode,
  type NgwExtension,
  type NodeViewFactory,
} from '@ngweditor/react'
import { buildAvailabilityHtml } from '../availabilityHtml'
import type { NgwEditorAdapter } from '../NgwEditorAdapter'

/**
 * NodeView 의 mousedown 분기에서 사용되는 좌표 / 셀렉터 helper.
 *
 * TinyMCE 평행 (`nine_tinymce_desktop.js` L4888~L4922) 의 tap/click 분기와 동일한 두 hit-test
 * 를 named function 으로 분리하여 mousedown listener 의 가독성을 보존한다.
 */
const CLOSE_HIT_SIZE = 20

const PICKER_SLOT_SELECTOR = '.nine-availability td > input + form > table'

function isCloseClick(
  wrapper: HTMLElement,
  target: HTMLElement | null,
  clientX: number,
  clientY: number,
): boolean {
  if (target !== wrapper) return false
  const rect = wrapper.getBoundingClientRect()
  const offsetX = clientX - rect.left
  const offsetY = clientY - rect.top
  return offsetX > rect.width - CLOSE_HIT_SIZE && offsetY < CLOSE_HIT_SIZE
}

function findPickerSlot(wrapper: HTMLElement, target: HTMLElement | null): Element | null {
  const slotTable = target?.closest(PICKER_SLOT_SELECTOR) ?? null
  return slotTable != null && wrapper.contains(slotTable) ? slotTable : null
}

/**
 * uuid-like id 발급. TinyMCE `nine_tinymce_desktop.js` L7281~L7289 의 guid() 와 동일 형식.
 * crypto.randomUUID 가 옛 브라우저에서 부재할 수 있어 Math.random 기반 fallback 을 그대로 이전.
 */
function genAvailabilityId(): string {
  const s4 = (): string =>
    Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1)
  return `id-${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`
}

type AvailabilityAttrs = {
  id: string
  dateTimeTableJson: string
  title: string
  tableDesc: string
  appDesc: string
  appLink: string
} & Record<string, unknown>

interface AvailabilityCommandPayload {
  dateTimeTableJson: string
  title?: string
  tableDesc?: string
  appDesc?: string
  appLink?: string
  id?: string
}

export function createAvailabilityExtension(adapter: NgwEditorAdapter): NgwExtension {
  // 어댑터 참조는 NodeView 의 cancel 콜백 흐름에서만 사용되며, 본 closure 자체는 그 외에
  // adapter 를 직접 사용하지 않는다. 단일 source-of-truth 보존을 위해 factory 형식.
  void adapter

  return {
    name: 'availability',

    nodes: {
      availability: {
        group: 'block',
        atom: true,
        selectable: false,
        attrs: {
          id: { default: '' },
          dateTimeTableJson: { default: '' },
          title: { default: '' },
          tableDesc: { default: '' },
          appDesc: { default: '' },
          appLink: { default: '' },
        },
        parseDOM: [
          // 신규 마커
          {
            tag: 'div.nine-availability',
            getAttrs: (dom) => {
              if (typeof dom === 'string') return false
              return {
                id: dom.getAttribute('data-id') ?? '',
                dateTimeTableJson: dom.getAttribute('data-time-tables') ?? '',
                title: dom.getAttribute('data-title') ?? '',
                tableDesc: dom.getAttribute('data-table-desc') ?? '',
                appDesc: dom.getAttribute('data-app-desc') ?? '',
                appLink: dom.getAttribute('data-app-link') ?? '',
              }
            },
          },
          // legacy TinyMCE (nine_tinymce_desktop.js L7219) + PR-N9a-2 후처리 self-contained 마크업.
          // 후자는 buildAvailabilityHtml 이 생성하므로 본 분기가 다시 attrs 로 회수해 round-trip
          // 정합성 보장. title 은 .nine-pg, tableDesc 는 table[aria-label], appDesc/appLink 는
          // app brand anchor 에서 추출.
          {
            tag: 'div.ms-outlook-mobile-availability-container',
            getAttrs: (dom) => {
              if (typeof dom === 'string') return false
              const titleElem = dom.querySelector('p.nine-pg')
              const tableElem = dom.querySelector('table[aria-label]')
              const appAnchor = dom.querySelector<HTMLAnchorElement>('a[href]')
              return {
                id: dom.getAttribute('id') ?? '',
                dateTimeTableJson: dom.getAttribute('data-time-tables') ?? '',
                title: titleElem?.textContent ?? '',
                tableDesc: tableElem?.getAttribute('aria-label') ?? '',
                appDesc: appAnchor?.textContent ?? '',
                appLink: appAnchor?.getAttribute('href') ?? '',
              }
            },
          },
        ],
        toDOM: (node) => {
          const attrs = node.attrs as Partial<AvailabilityAttrs>
          return [
            'div',
            {
              class: 'nine-availability',
              'data-id': String(attrs.id ?? ''),
              'data-time-tables': String(attrs.dateTimeTableJson ?? ''),
              'data-title': String(attrs.title ?? ''),
              'data-table-desc': String(attrs.tableDesc ?? ''),
              'data-app-desc': String(attrs.appDesc ?? ''),
              'data-app-link': String(attrs.appLink ?? ''),
              contenteditable: 'false',
            },
          ]
        },
      },
    },

    commands: {
      addSendAvailability: (state, dispatch, _view, ...args) => {
        if (dispatch == null) return true

        const arg = args[0]
        if (typeof arg !== 'object' || arg === null) return false
        const payload = arg as AvailabilityCommandPayload
        const nodeType = state.schema.nodes.availability
        if (nodeType == null) return false

        const newAttrs: AvailabilityAttrs = {
          id: payload.id != null && payload.id.length > 0 ? payload.id : genAvailabilityId(),
          dateTimeTableJson: payload.dateTimeTableJson,
          title: payload.title ?? '',
          tableDesc: payload.tableDesc ?? '',
          appDesc: payload.appDesc ?? '',
          appLink: payload.appLink ?? '',
        }

        // payload.id 가 제공되면 기존 노드 갱신 시도
        if (payload.id != null && payload.id.length > 0) {
          let updatedPos: number | null = null
          state.doc.descendants((node, pos) => {
            if (updatedPos !== null) return false
            if (node.type.name === 'availability' && node.attrs.id === payload.id) {
              updatedPos = pos
              return false
            }
            return true
          })
          if (updatedPos !== null) {
            const tr = state.tr.setNodeMarkup(updatedPos, undefined, newAttrs)
            dispatch(tr.scrollIntoView())
            return true
          }
        }

        // 신규 노드 삽입 (현 selection 위치) — atom 노드 앞·뒤에 empty paragraph 동반 삽입.
        //
        // PR-N9a-2 §15-C 재설계 (2026-05-19 사용자 보고 후): 시간표 위·아래에 빈 줄을 표시하면서
        // PM 의 enter handling 회귀 (TransformError: Inserted content deeper than insertion
        // position) 를 회피하려면 NodeView wrapper 자식이 아니라 **PM doc 차원의 paragraph 노드**
        // 가 atom 노드 양옆에 있어야 한다. NodeView 자식은 시각효과 외엔 PM 모델에 반영되지 않아
        // cursor / enter 가 동작하지 않는다.
        //
        // 빈 paragraph 가 양옆에 있으면:
        //   - 사용자 클릭/cursor 가 자연스럽게 paragraph 안에 위치
        //   - Enter 키가 paragraph 를 split 하여 정상 줄바꿈
        //   - 글로벌 CSS / NgwEditor base styles 의 paragraph 마진/패딩이 적용되어 시각적 빈 줄
        //   - 송신 시 getHTML 결과에 `<p><br></p>` 가 자연 직렬화 → 받는 측 메일 클라이언트도 빈 줄
        const atomNode = nodeType.create(newAttrs)
        const paraType = state.schema.nodes.paragraph

        const beforePara = paraType?.createAndFill()
        const afterPara = paraType?.createAndFill()
        const insertNodes: ProseMirrorNode[] =
          beforePara != null && afterPara != null ? [beforePara, atomNode, afterPara] : [atomNode]

        const { from, to } = state.selection
        const tr = state.tr.replaceWith(from, to, insertNodes)
        dispatch(tr.scrollIntoView())
        return true
      },
    },
  }
}

/**
 * availability NodeView — title/tableDesc 표시 + 좌표 기반 cancel 클릭 감지.
 *
 * 시각화 (outline + 우상단 close 버튼) 는 글로벌 CSS 의 `.nine-availability` selector 와
 * `.nine-availability::after` 가상 요소로 처리한다. 본 NodeView 는 DOM 구조만 책임지고
 * cancel 동작은 wrapper-level mousedown 의 좌표 검사 (TinyMCE 의
 * [nine_tinymce_desktop.js L4888-L4914](../../../../../apps/owma/public/editor/nine_tinymce_desktop.js#L4888-L4914)
 * 와 동일 — 우상단 20×20 영역) 로 결선한다.
 *
 * NodeView 의 dom 노드 lifecycle 은 ProseMirror 가 관리 — destroy hook 없이도 dom 제거 시
 * 이벤트 리스너 자동 정리 (DOM 표준 동작).
 */
export function createAvailabilityNodeView(adapter: NgwEditorAdapter): NodeViewFactory {
  return (node, view, getPos) => {
    const wrapper = document.createElement('div')
    wrapper.className = 'nine-availability'
    wrapper.setAttribute('contenteditable', 'false')
    wrapper.setAttribute('data-id', String(node.attrs.id ?? ''))

    // PR-N9a-2 보강: 작성자 화면도 수신자와 동일한 self-contained 마크업으로 시각화 (WYSIWYG).
    // buildAvailabilityHtml 이 모든 사용자 입력 (title/tableDesc/appDesc/appLink + slot label)을
    // HTML escape 하므로 안전. DOMParser 로 파싱 후 자식 노드를 이전 — script 실행 없음.
    // JSON 파싱 실패 또는 빈 데이터일 때는 기존 텍스트 fallback 으로 최소 표시 유지.
    //
    // PR-N9a-2 §15-C 재설계 (2026-05-19): NodeView wrapper 자식의 빈 줄 (`<p><br></p>`) 은 PM
    // 모델에 반영되지 않아 cursor / Enter 가 동작하지 않고 시각효과도 없다. 따라서 시간표 위·아래
    // 빈 줄은 `addSendAvailability` 명령에서 atom 노드 양옆에 empty paragraph 를 PM doc 차원에
    // 직접 추가하는 방식으로 대체 (위 명령 본체 참조). NodeView 는 시간표 본체 마크업만 unwrap.
    // 따라서 buildAvailabilityHtml 호출 시 `wrapWithSpace: false` (기본) — wrap 중복 방지.
    const richHtml = buildAvailabilityHtml({
      id: String(node.attrs.id ?? ''),
      dateTimeTableJson: String(node.attrs.dateTimeTableJson ?? ''),
      title: String(node.attrs.title ?? ''),
      tableDesc: String(node.attrs.tableDesc ?? ''),
      appDesc: String(node.attrs.appDesc ?? ''),
      appLink: String(node.attrs.appLink ?? ''),
    })
    if (richHtml != null) {
      const tempBody = new DOMParser().parseFromString(richHtml, 'text/html').body
      // PR-N9a-2 §15-C 재설계 — `tempBody.children` 의 live HTMLCollection 변형 회피를 위해
      // 스냅샷 후 element 타입별 분기:
      //   - <div.ms-outlook-mobile-availability-container>: 그 자식만 wrapper 로 이전 (unwrap).
      //     container 의 inline style (max-width / margin / padding / border-radius) 이 wrapper
      //     의 글로벌 .nine-availability CSS 와 누적되어 double padding 되는 회귀 방지.
      //   - 그 외 element: 정상 경로엔 없음 (wrapWithSpace: false 라 sibling 추가 안 됨). 만일을
      //     대비해 sibling 으로 이전.
      //   - comment / text 노드: 무시.
      const elements = Array.from(tempBody.children)
      for (const elem of elements) {
        if (elem.classList.contains('ms-outlook-mobile-availability-container')) {
          while (elem.firstChild != null) {
            wrapper.appendChild(elem.firstChild)
          }
        } else {
          wrapper.appendChild(elem)
        }
      }
    } else {
      const title = document.createElement('div')
      title.className = 'nine-availability-title'
      title.textContent = String(node.attrs.title ?? '')

      const tableDesc = document.createElement('div')
      tableDesc.className = 'nine-availability-table-desc'
      tableDesc.textContent = String(node.attrs.tableDesc ?? '')

      wrapper.appendChild(title)
      wrapper.appendChild(tableDesc)
    }

    // 두 가지 mousedown 분기 (TinyMCE [nine_tinymce_desktop.js L4888-L4922] tap/click 평행):
    //   1) wrapper 우상단 20×20 영역 (= 글로벌 CSS 의 ::after close 버튼) → clearAvailability
    //   2) 시간 슬롯 본체 영역 (selector 매칭) → showAvailabilityPicker (재편집 진입)
    wrapper.addEventListener('mousedown', (e) => {
      const target = e.target as HTMLElement | null

      if (isCloseClick(wrapper, target, e.clientX, e.clientY)) {
        e.preventDefault()
        e.stopPropagation()

        const rawId = node.attrs.id
        const id = typeof rawId === 'string' && rawId.length > 0 ? rawId : undefined

        const handler = adapter.getHandler()
        handler?.clearAvailability?.(id)

        const pos = getPos()
        if (pos !== undefined) {
          const tr = view.state.tr.delete(pos, pos + node.nodeSize)
          view.dispatch(tr)
        }
        return
      }

      if (findPickerSlot(wrapper, target) != null) {
        e.preventDefault()
        e.stopPropagation()

        const rawJson = node.attrs.dateTimeTableJson
        const json = typeof rawJson === 'string' ? rawJson : ''
        const rawId = node.attrs.id
        const id = typeof rawId === 'string' && rawId.length > 0 ? rawId : undefined

        if (json.length > 0) {
          const handler = adapter.getHandler()
          handler?.showAvailabilityPicker?.(json, id)
        }
      }
    })

    return {
      dom: wrapper,
      ignoreMutation: () => true,
      stopEvent: (event) => {
        if (event.type !== 'mousedown') return false
        const me = event as MouseEvent
        const target = me.target as HTMLElement | null

        // close 영역 — wrapper 자체 + 우상단 좌표.
        if (target === wrapper) {
          const rect = wrapper.getBoundingClientRect()
          const offsetX = me.clientX - rect.left
          const offsetY = me.clientY - rect.top
          if (offsetX > rect.width - 20 && offsetY < 20) return true
        }

        // 시간 슬롯 본체 영역 — picker 재진입.
        const slotTable = target?.closest('.nine-availability td > input + form > table') ?? null
        if (slotTable != null && wrapper.contains(slotTable)) return true

        return false
      },
    }
  }
}
